import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchAccounts,
  fetchAccountsSuccess,
  fetchAccountsFailure,
  fetchAccountById,
  fetchAccountByIdSuccess,
  fetchAccountByIdFailure,
  fetchRoleById,
  fetchRoleByIdSuccess,
  fetchRoleByIdFailure,
  addAccount,
  addAccountSuccess,
  addAccountFailure,
  updateAccount,
  updateAccountSuccess,
  updateAccountFailure,
  deleteAccount,
  deleteAccountSuccess,
  deleteAccountFailure,
  bulkDeleteAccounts,
  bulkDeleteAccountsSuccess,
  bulkDeleteAccountsFailure,
  requestOtpLogin,
  requestOtpLoginSuccess,
  requestOtpLoginFailure,
} from './slice';

/**
 * Translate error message from backend
 * Maps common backend error messages to i18n translation keys
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('accounts.errors.unknown');

  // Common error message mappings
  const errorMap = {
    'Username already exists': 'accounts.errors.usernameExists',
    'Email already exists': 'accounts.errors.emailExists',
    'Invalid credentials': 'accounts.errors.invalidCredentials',
    'Account not found': 'accounts.errors.accountNotFound',
    'Failed to add account': 'accounts.errors.addFailed',
    'Failed to update account': 'accounts.errors.updateFailed',
    'Failed to delete account': 'accounts.errors.deleteFailed',
    'Failed to fetch accounts': 'accounts.errors.fetchFailed',
    'Failed to fetch init data': 'accounts.errors.initFailed',
  };

  // Check if error message matches a known pattern
  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  // If no mapping found, return original message
  return errorMessage;
}

// Worker saga: Fetch init data
function* handleFetchInit() {
  try {
    const response = yield call(api.get, config.endpoints.users.init);
    // Debug: Log response to check structure
    console.log('UserInit response:', response);
    console.log('Positions in response:', response.positions);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchInitFailure(translatedError));
  }
}

// Worker saga: Fetch accounts
function* handleFetchAccounts(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, and filters from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterUsername, filterRoles, filterUnits } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterUsername) {
      params.username = filterUsername;
    }
    if (filterRoles && filterRoles.length > 0) {
      params.roleIds = filterRoles;
    }
    if (filterUnits && filterUnits.length > 0) {
      params.unitIds = filterUnits;
    }

    // API will serialize arrays as repeated params (roleIds=1&roleIds=2) via global paramsSerializer config
    const response = yield call(api.get, config.endpoints.users.list, params);
    // Pass the whole response (including pagination info)
    yield put(fetchAccountsSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchAccountsFailure(translatedError));
  }
}

// Worker saga: Fetch account by ID
function* handleFetchAccountById(action) {
  try {
    const accountId = action.payload;
    const response = yield call(api.get, `${config.endpoints.users.list}/${accountId}`);
    yield put(fetchAccountByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchAccountByIdFailure(translatedError));
  }
}

// Worker saga: Fetch role by ID (for permissions display)
function* handleFetchRoleById(action) {
  try {
    const roleId = action.payload;
    // Call GET /roles/{id} to get role details with permissions
    const response = yield call(api.get, `${config.endpoints.roles.getById}/${roleId}`);
    yield put(fetchRoleByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchRoleByIdFailure(translatedError));
  }
}

// Worker saga: Add account
function* handleAddAccount(action) {
  try {
    const response = yield call(api.post, config.endpoints.users.create, action.payload);
    yield put(addAccountSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addAccountFailure(translatedError));
  }
}

// Worker saga: Update account
function* handleUpdateAccount(action) {
  try {
    const { id } = action.payload;
    // Include id in the request body
    const response = yield call(api.put, `${config.endpoints.users.update}/${id}`, action.payload);
    yield put(updateAccountSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateAccountFailure(translatedError));
  }
}

// Worker saga: Delete account
function* handleDeleteAccount(action) {
  try {
    const accountId = action.payload;
    yield call(api.delete, `${config.endpoints.users.delete}/${accountId}`);
    yield put(deleteAccountSuccess(accountId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteAccountFailure(translatedError));
  }
}

// Worker saga: Bulk delete accounts
function* handleBulkDeleteAccounts(action) {
  try {
    const accountIds = action.payload;
    // Call API DELETE /users?ids=1&ids=2&ids=3
    // Use custom paramsSerializer to format array as repeated params
    yield call(api.delete, config.endpoints.users.delete, {
      params: { ids: accountIds },
      paramsSerializer: {
        indexes: null, // Use repeat format: ?ids=1&ids=2 instead of ?ids[0]=1&ids[1]=2
      },
    });
    yield put(bulkDeleteAccountsSuccess(accountIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteAccountsFailure(translatedError));
  }
}

// Worker saga: Request OTP Login
function* handleRequestOtpLogin(action) {
  try {
    const userId = action.payload;
    // Call API GET /users/{id}/request-otp-login
    const response = yield call(api.get, `${config.endpoints.users.list}/${userId}/request-otp-login`);
    yield put(requestOtpLoginSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(requestOtpLoginFailure(translatedError));
  }
}

// Watcher saga
export function* watchAccountsActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchAccounts.type, handleFetchAccounts);
  yield takeLatest(fetchAccountById.type, handleFetchAccountById);
  yield takeLatest(fetchRoleById.type, handleFetchRoleById);
  yield takeLatest(addAccount.type, handleAddAccount);
  yield takeLatest(updateAccount.type, handleUpdateAccount);
  yield takeLatest(deleteAccount.type, handleDeleteAccount);
  yield takeLatest(bulkDeleteAccounts.type, handleBulkDeleteAccounts);
  yield takeLatest(requestOtpLogin.type, handleRequestOtpLogin);
}
